import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaPlus, FaTimes, FaPen } from "react-icons/fa";
import { useCurrency } from "../../../../../context/currencyContext";

const API_URL = "https://hms.backend.hiragroupcloud.com/api/cases";

const DraftInvoice = ({ show, onHide, caseId, caseNumber }) => {
  const { formatAmount } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [caseData, setCaseData] = useState(null);
  const [units, setUnits] = useState({});
  const [prices, setPrices] = useState({});
  const [additionalLines, setAdditionalLines] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [isTaxable, setIsTaxable] = useState(false);
  
  // Use ref to prevent unnecessary re-renders
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);

  // Memoized subtotal calculation - only recalculates when dependencies change
  const subtotal = useMemo(() => {
    const totalProcedures = Object.entries(units).reduce((sum, [i, unitCount]) => {
      const pricePerUnit = prices[i] || 0;
      return sum + (unitCount * pricePerUnit);
    }, 0);

    const addTotal = additionalLines.reduce((sum, l) => sum + (l.amount || 0), 0);
    return totalProcedures + addTotal;
  }, [units, prices, additionalLines]);

  // Cleanup function
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Optimized fetch function with AbortController
  const fetchCase = useCallback(async () => {
    if (!show || !caseId) return;
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_URL}/cases/${caseId}`, {
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) throw new Error("Case not found");
      
      const data = await response.json();
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        setCaseData(data);
        
        // Batch state updates to reduce re-renders
        const initialUnits = {};
        const initialPrices = {};
        
        if (data.procedures?.length) {
          data.procedures.forEach((proc, i) => {
            const calcUnits = proc.calculatedUnits || 1;
            initialUnits[i] = calcUnits;
            initialPrices[i] = (proc.price || 0) / calcUnits;
          });
        }
        
        // Batch these updates together
        setUnits(initialUnits);
        setPrices(initialPrices);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && isMounted.current) {
        setError(err.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [show, caseId]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  // Memoized handlers to prevent recreation on each render
  const handleUnitChange = useCallback((index, value) => {
    const unitValue = Math.max(1, Number(value) || 1);
    setUnits(prev => ({ ...prev, [index]: unitValue }));
  }, []);

  const handlePriceChange = useCallback((index, value) => {
    const priceValue = Math.max(0, Number(value) || 0);
    setPrices(prev => ({ ...prev, [index]: priceValue }));
  }, []);

  const handleAddLine = useCallback(() => {
    setAdditionalLines(prev => [...prev, { id: Date.now(), amount: 0, taxable: false }]);
  }, []);

  const handleDeleteLine = useCallback((id) => {
    setAdditionalLines(prev => prev.filter(l => l.id !== id));
  }, []);

  const handleEditLine = useCallback((line) => {
    setEditingLine(line);
    setIsTaxable(line.taxable || false);
    setShowModal(true);
  }, []);

  const handleSaveModal = useCallback(() => {
    if (editingLine) {
      setAdditionalLines(prev =>
        prev.map(l =>
          l.id === editingLine.id ? { ...l, taxable: isTaxable } : l
        )
      );
    }
    setShowModal(false);
    setEditingLine(null);
  }, [editingLine, isTaxable]);

  const handleLineAmountChange = useCallback((id, value) => {
    setAdditionalLines(prev =>
      prev.map(l => (l.id === id ? { ...l, amount: Number(value) || 0 } : l))
    );
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingLine(null);
  }, []);

  // Memoized calculations
  const calculateProcedureAmount = useCallback((index) => {
    const unitCount = units[index] || 1;
    const pricePerUnit = prices[index] || 0;
    return unitCount * pricePerUnit;
  }, [units, prices]);

  // Optimized save function with debouncing
  const handleSaveDraft = useCallback(async () => {
    if (!caseData) return;
    
    setLoading(true);
    
    const updatedProcedures = caseData.procedures?.map((proc, i) => {
      const newUnits = units[i] || proc.calculatedUnits || 1;
      const newPricePerUnit = prices[i] || (proc.price / (proc.calculatedUnits || 1)) || 0;
      const newTotalPrice = newUnits * newPricePerUnit;
      return {
        ...proc,
        calculatedUnits: newUnits,
        price: newTotalPrice,
      };
    }) || [];

    const totalProcedures = updatedProcedures.reduce((sum, proc) => sum + (proc.price || 0), 0);
    
    const invoiceData = {
      caseId,
      caseNumber,
      procedures: updatedProcedures,
      units,
      prices,
      additionalLines,
      subtotal,
    };

    // Save to localStorage
    localStorage.setItem("invoiceData", JSON.stringify(invoiceData));

    try {
      const response = await fetch(`${API_URL}/${caseId}/total-amount`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ totalAmount: totalProcedures, procedures: updatedProcedures }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update case in database');
      }
      
      alert("Invoice Draft Saved Successfully! Case updated with new units, prices, and total amount.");
      onHide();
    } catch (error) {
      console.error('Error updating case:', error);
      alert('Invoice draft saved locally but failed to update case in database.');
    } finally {
      setLoading(false);
    }
  }, [caseData, units, prices, additionalLines, subtotal, caseId, caseNumber, onHide]);

  // Memoized styles to prevent recreation
  const boxStyle = useMemo(() => ({
    width: "70px",
    height: "36px",
    textAlign: "center",
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "4px",
    fontSize: "14px",
  }), []);

  const descriptionStyle = useMemo(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1.3",
    textAlign: "center",
  }), []);

  const descriptionHeading = useMemo(() => ({ fontWeight: "bold", marginBottom: "8px" }), []);
  const smallText = useMemo(() => ({ fontSize: "13px", color: "#555", marginTop: "8px" }), []);

  // Memoize procedures list to prevent unnecessary re-renders
  const proceduresList = useMemo(() => {
    if (!caseData?.procedures) return null;
    
    return caseData.procedures.map((proc, i) => (
      <div
        key={`proc-${i}`}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr",
          borderBottom: "1px solid #ddd",
          textAlign: "center",
          padding: "20px 0",
          alignItems: "center",
        }}
      >
        <div>
          <input
            type="number"
            value={units[i] || proc.calculatedUnits || 1}
            min={1}
            onChange={(e) => handleUnitChange(i, e.target.value)}
            style={boxStyle}
          />
        </div>
        <div style={descriptionStyle}>
          <div style={descriptionHeading}>{proc.procedure}</div>
          <div style={smallText}>
            {proc.teeth && <div>Teeth: {proc.teeth}</div>}
            {proc.shade && <div>Shade: {proc.shade}</div>}
          </div>
        </div>
        <div>
          <input
            type="number"
            value={prices[i] ?? (proc.price / (proc.calculatedUnits || 1)) ?? 0}
            min={0}
            step="0.01"
            onChange={(e) => handlePriceChange(i, e.target.value)}
            style={boxStyle}
          />
        </div>
        <div>
          <input
            type="number"
            value={calculateProcedureAmount(i)}
            readOnly
            style={{...boxStyle, backgroundColor: "#f9f9f9"}}
          />
        </div>
      </div>
    ));
  }, [caseData, units, prices, boxStyle, descriptionStyle, descriptionHeading, smallText, handleUnitChange, handlePriceChange, calculateProcedureAmount]);

  // Memoize additional lines list
  const additionalLinesList = useMemo(() => {
    return additionalLines.map((line) => (
      <div
        key={line.id}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          padding: "10px 0",
          borderBottom: "1px dashed #eee",
        }}
      >
        <input
          type="number"
          value={line.amount}
          onChange={(e) => handleLineAmountChange(line.id, e.target.value)}
          style={{ ...boxStyle, width: "90px" }}
          placeholder="Amount"
        />
        <button
          onClick={() => handleEditLine(line)}
          style={{
            backgroundColor: "#004aad",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            width: "32px",
            height: "32px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaPen style={{ fontSize: "12px" }} />
        </button>
        <button
          onClick={() => handleDeleteLine(line.id)}
          style={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            width: "32px",
            height: "32px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaTimes style={{ fontSize: "12px" }} />
        </button>
      </div>
    ));
  }, [additionalLines, boxStyle, handleLineAmountChange, handleEditLine, handleDeleteLine]);

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1050,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "850px",
          maxHeight: "90vh",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          overflow: "hidden",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#004aad",
            color: "white",
            padding: "15px 20px",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Invoice Draft - Case #{caseNumber}</span>
          <span
            onClick={onHide}
            style={{ cursor: "pointer", fontSize: "18px", fontWeight: "bold" }}
          >
            ✕
          </span>
        </div>

        <div style={{ padding: "15px", overflowY: "auto", maxHeight: "70vh" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>Loading case...</p>
            </div>
          )}
          {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

          {!loading && !error && (
            <>
              <h3
                style={{
                  color: "#333",
                  fontSize: "18px",
                  marginBottom: "10px",
                  borderBottom: "2px solid #004aad",
                  paddingBottom: "5px",
                  textAlign: "left",
                }}
              >
                Invoice Details
              </h3>

              {/* HEADER */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr",
                  backgroundColor: "#f6f6f6",
                  border: "1px solid #ccc",
                  fontWeight: "bold",
                  padding: "10px",
                  textAlign: "center",
                }}
              >
                <div>Units</div>
                <div>Description</div>
                <div>Price/Unit</div>
                <div>Amount</div>
              </div>

              {/* PROCEDURE ROWS */}
              {proceduresList}

              {/* ADDITIONAL LINES */}
              <div
                style={{
                  borderBottom: "1px solid #ccc",
                  padding: "12px 0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "20px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Additional Lines</span>
                <button
                  onClick={handleAddLine}
                  style={{
                    backgroundColor: "#004aad",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    fontSize: "18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaPlus />
                </button>
              </div>

              {additionalLinesList}

              {/* TOTALS */}
              <div style={{ marginTop: "20px", borderTop: "2px solid #004aad", paddingTop: "15px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <span>Subtotal</span>
                  <span style={{ fontWeight: "bold" }}>{formatAmount(subtotal)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <span>Taxes</span>
                  <span style={{ fontWeight: "bold" }}>{formatAmount(0)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    backgroundColor: "#f6f6f6",
                    borderRadius: "4px",
                  }}
                >
                  <span>Total</span>
                  <span>{formatAmount(subtotal)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "15px 20px",
            backgroundColor: "#f8f9fa",
            gap: "10px",
            borderTop: "1px solid #eee",
          }}
        >
          <button
            onClick={onHide}
            disabled={loading}
            style={{
              backgroundColor: "#E0E0E0",
              color: "#333",
              border: "none",
              borderRadius: "4px",
              padding: "10px 18px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
              opacity: loading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={loading}
            style={{
              backgroundColor: "#004aad",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 18px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "Saving..." : "Save Draft"}
          </button>
        </div>
      </div>

      {/* TAX MODAL */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              width: "360px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                backgroundColor: "#004aad",
                color: "#fff",
                padding: "12px 16px",
                fontWeight: "bold",
                fontSize: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Manage Line Item Tax</span>
              <span
                onClick={handleCloseModal}
                style={{ cursor: "pointer", fontSize: "20px", fontWeight: "bold" }}
              >
                ×
              </span>
            </div>
            <div style={{ padding: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                Is Line Item Taxable?
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={isTaxable}
                  onChange={(e) => setIsTaxable(e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "#004aad" }}
                />
                <span style={{ fontSize: "14px" }}>Yes, this line item is taxable</span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "0 20px 20px",
              }}
            >
              <button
                onClick={handleCloseModal}
                style={{
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveModal}
                style={{
                  backgroundColor: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftInvoice;